import { ProductFormData } from '@/@types/Products'
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect'
import { Input } from '@/components/shared/Input/Input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import {
  defaultRefundDeadlineDays,
  getRefundDeadlineDaysSelectOptions,
  isAllowedRefundDeadlineDays,
  normalizeRefundDeadlineDays,
} from '@/constants/refundDeadline'
import { cn } from '@/lib/utils'
import { PAGE_INSTRUCTIONS_URL } from '@/utils/constants'
import { normalizeUrlOnBlur, normalizeUrlWhenPaste } from '@/utils/normalize'
import { Check, ChevronsUpDown, Eye } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Step2Props {
  watch: UseFormWatch<ProductFormData>
  register: UseFormRegister<ProductFormData>
  errors: FieldErrors<ProductFormData>
  setValue: UseFormSetValue<ProductFormData>
  clearErrors: UseFormClearErrors<ProductFormData>
  defaultValues: ProductFormData
  handleBack: () => void
  handleNext: () => void
  categories: any[]
  isFirstProduct?: boolean
  isCheckingFirstProduct?: boolean
}

export default function Step2({
  watch,
  register,
  errors,
  setValue,
  clearErrors,
  defaultValues,
  handleBack,
  handleNext,
  categories,
  isFirstProduct = false,
  isCheckingFirstProduct = false,
}: Step2Props) {
  const { t } = useTranslation(['common', 'onboarding'])
  const [categoryOpen, setCategoryOpen] = useState(false)

  const [filteredCategories, setFilteredCategories] = useState(categories)

  const selectedCategory = categories?.find(
    (cat) => cat.value === watch('category_id'),
  )

  const productName = watch('name')?.trim()
  const offerName = watch('offer_name')?.trim()
  const productType = watch('product_type')
  const isPhysicalLikeOffer =
    productType === 'pharmaceutical' || productType === 'physical'
  const offerNamePlaceholder = t(
    isPhysicalLikeOffer ? 'offerNamePlaceholder' : 'offerNamePlaceholderDigital',
  )
  const offerNameTooltip = t(
    isPhysicalLikeOffer ? 'offerNameTooltip' : 'offerNameTooltipDigital',
  )

  const refundDeadlineOptions = getRefundDeadlineDaysSelectOptions(t, productType)
  const defaultRefundDays = defaultRefundDeadlineDays(productType)

  useEffect(() => {
    const current = Number(watch('refund_deadline_days'))
    if (!isAllowedRefundDeadlineDays(current, productType)) {
      setValue('refund_deadline_days', defaultRefundDays)
    }
  }, [defaultRefundDays, productType, setValue, watch])

  const checkoutPreview =
    productName && offerName
      ? `${productName} – ${offerName}`
      : productName || offerName || t('offerNameCheckoutPreviewPlaceholder')

  const filterCategories = (query: string) => {
    const result = categories.filter((category) =>
      category.label.toLowerCase().includes(query.toLowerCase()),
    )
    setFilteredCategories(result)
  }

  useEffect(() => {
    setFilteredCategories(categories)
  }, [categories])

  useEffect(() => {
    if (watch('category_id')) {
      clearErrors('category_id')
    }
  }, [watch('category_id'), clearErrors, setValue])

  function getProductTypeText(type: string, t: any) {
    switch (type) {
      case 'digital':
        return t('digital_product_text')
      case 'physical':
        return t('physical_product_text')
      case 'pharmaceutical':
        return t('nutra_product_text')
      default:
        return ''
    }
  }

  const handleUrlBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    fieldName: 'sales_page' | 'support_url' | 'privacy_terms_url',
  ) => {
    const normalizedValue = normalizeUrlOnBlur(
      e.target.value.trim().replace(/\s/g, ''),
    )
    e.target.value = normalizedValue
    setValue(fieldName, normalizedValue)
    if (isFirstProduct && fieldName === 'sales_page') {
      setValue('page_url', normalizedValue)
    }
  }

  const handleThankyouUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value.trim().replace(/\s/g, '')
    if (!raw || raw === 'https://' || raw === 'http://') {
      e.target.value = ''
      setValue('thankyou_url', '')
      clearErrors('thankyou_url')
      return
    }
    const normalized = normalizeUrlOnBlur(raw)
    e.target.value = normalized
    setValue('thankyou_url', normalized)
  }

  const thankyouUrlField = register('thankyou_url', {
    setValueAs: (v) =>
      typeof v === 'string' ? v.trim().replace(/\s/g, '') : v,
  })
  const {
    onBlur: thankyouUrlRhfOnBlur,
    ...thankyouUrlInputProps
  } = thankyouUrlField

  return (
    <section className="w-full max-w-lg">
      <h1 className="text-2xl font-bold text-center mb-6">
        {t('step2Title')}{' '}
        <p className="text-interactive">
          {getProductTypeText(watch('product_type'), t)}
        </p>
      </h1>

      <div className="space-y-6">
        {/* Mantém page_url no form state (usado no 1º produto) */}
        <input type="hidden" {...register('page_url')} />

        <Input
          label={t('productNameLabel')}
          {...register('name')}
          placeholder={t('productNamePlaceholder')}
          helperText={errors?.name?.message}
          data-cy="product-name"
        />

        <div className="space-y-2">
          <Input
            label={t('offerNameLabel')}
            {...register('offer_name')}            
            placeholder={offerNamePlaceholder}
            helperText={errors?.offer_name?.message}
            tooltip={offerNameTooltip}
            data-cy="offer-name"
          />

          <div className="flex items-start gap-2 rounded-md border border-colorBorder bg-[#09090B] px-3 py-2.5">
            <Eye className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
            <p className="text-sm leading-relaxed">
              <span className="text-muted">{t('offerNameCheckoutPreview')}</span>{' '}
              <span className="font-medium text-interactive">{checkoutPreview}</span>
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 w-full">
          <Label>{t('category')}</Label>

          <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={categoryOpen}
                className={cn(
                  'w-full justify-between bg-[#09090b] text-muted text-sm',
                  errors?.category_id ? '!border-error' : 'border-colorBorder',
                )}
                disabled={categories?.length === 0}
                data-cy="category-select"
              >
                {selectedCategory?.label || t('categoryPlaceholder')}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="p-0 w-full">
              <Command>
                <CommandInput
                  placeholder={t('categoryPlaceholder')}
                  onValueChange={filterCategories}
                />
                <CommandList>
                  <CommandGroup>
                    {filteredCategories?.map((category) => (
                      <CommandItem
                        key={category.value}
                        value={category.label.toLowerCase()}
                        onSelect={() => {
                          setValue('category_id', category.value)
                          clearErrors('category_id')
                          const type = (category.type || category.category_type || watch('product_type') || '').toLowerCase()
                          const current = Number(watch('refund_deadline_days') || 0)
                          if (!isAllowedRefundDeadlineDays(current, type)) {
                            setValue('refund_deadline_days', defaultRefundDeadlineDays(type))
                          }
                          setCategoryOpen(false)
                        }}
                        data-cy="category-option"
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            watch('category_id') === category.value
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        {category.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {errors?.category_id?.message && (
            <p className="text-sm text-error">{errors.category_id.message}</p>
          )}
        </div>

        {defaultValues?.product_type === 'pharmaceutical' && (
          <Input
            label={'SKU'}
            {...register('sku')}
            placeholder={t('skuPlaceholder')}
            helperText={errors?.sku?.message}
            data-cy="sku"
          />
        )}

        <div className="flex gap-2 flex-col">
          <Label htmlFor="description">{t('productDescriptionLabel')}</Label>
          <Textarea
            {...register('description')}
            placeholder={t('productDescriptionPlaceholder')}
            maxLength={500}
            className={`focus:outline-none ring-0 focus-visible:outline-none ${
              errors?.description ? 'border-error' : 'border-colorBorder'
            }`}
            data-cy="product-description"
          />
          <p className="text-muted text-xs -mt-1">
            {defaultValues?.description?.length}/500
          </p>
          {errors?.description?.message && (
            <p className="text-sm text-error">{errors.description.message}</p>
          )}
        </div>

        <CustomSelect
          label={t('refundDeadlineDaysLabel', 'Prazo de reembolso (dias)')}
          placeholder={t('refundDeadlineDaysPlaceholder', 'Selecione o prazo')}
          options={refundDeadlineOptions}
          value={String(
            normalizeRefundDeadlineDays(
              watch('refund_deadline_days'),
              productType,
            ),
          )}
          onValueChange={(value) => {
            setValue(
              'refund_deadline_days',
              normalizeRefundDeadlineDays(value, productType),
              { shouldValidate: true },
            )
            clearErrors('refund_deadline_days')
          }}
          helperText={errors?.refund_deadline_days?.message}
          dataCy="refund-deadline-days"
          id="refundDeadlineDays"
        />

        {!isFirstProduct && (
          <Input
            label={t('salesPageLabel')}
            {...register('sales_page')}
            placeholder={t('salesPagePlaceholder')}
            helperText={errors?.sales_page?.message}
            onFocus={(e) => {
              if (!e.target.value) {
                e.target.value = 'https://'
                setValue('sales_page', 'https://')
              }
            }}
            onPaste={(e) => {
              const finalValue = normalizeUrlWhenPaste(e)
              setValue('sales_page', finalValue)
            }}
            data-cy="sales-page"
          />
        )}

        <Input
          label={`${t('common:thankyouUrlLabel')} (${t('common:optional')})`}
          {...thankyouUrlInputProps}
          placeholder={t('common:thankyouUrlPlaceholder')}
          helperText={errors?.thankyou_url?.message}
          subLabel={t('common:thankyouUrlDescription')}
          onFocus={(e) => {
            if (!e.target.value) {
              e.target.value = 'https://'
              setValue('thankyou_url', 'https://')
            }
          }}
          onBlur={(e) => {
            handleThankyouUrlBlur(e)
            void thankyouUrlRhfOnBlur(e)
          }}
          onPaste={(e) => {
            const finalValue = normalizeUrlWhenPaste(e)
            setValue('thankyou_url', finalValue)
          }}
          data-cy="thankyou-url"
        />

        {isFirstProduct && (
          <section className="border-t border-t-colorBorder mt-8 pt-8 space-y-6">
            <Alert className="p-4">
              <AlertTitle className="mb-4">{t('onboarding:infos')}</AlertTitle>
              <AlertDescription className="">
                <ul className="">
                  <li>• {t('onboarding:user_url_pages_description1')}</li>
                  <li>• {t('onboarding:user_url_pages_description2')}</li>
                  <li>
                    • {t('onboarding:user_url_pages_description3')}
                    <a
                      href={PAGE_INSTRUCTIONS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <strong> {t('onboarding:click_here')}</strong>
                    </a>
                  </li>
                </ul>{' '}
              </AlertDescription>
            </Alert>

            <Input
              {...register('sales_page', {
                setValueAs: (v) => v?.trim().replace(/\s/g, ''),
              })}
              label={t('onboarding:user_sales_page')}
              placeholder={t('onboarding:user_sales_page_placeholder')}
              helperText={errors?.sales_page?.message}
              data-cy="product-url-input"
              className="mt-6 lg:mt-0"
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://'
                  setValue('sales_page', 'https://')
                  setValue('page_url', 'https://')
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e)
                setValue('sales_page', finalValue)
                setValue('page_url', finalValue)
              }}
              onBlur={(e) => handleUrlBlur(e, 'sales_page')}
            />

            <Input
              {...register('support_url', {
                setValueAs: (v) => v?.trim().replace(/\s/g, ''),
              })}
              label={t('onboarding:user_support_page')}
              placeholder={t('onboarding:user_support_page_placeholder')}
              helperText={errors?.support_url?.message}
              data-cy="support-url-input"
              className="mt-6 lg:mt-0"
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://'
                  setValue('support_url', 'https://')
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e)
                setValue('support_url', finalValue)
              }}
              onBlur={(e) => handleUrlBlur(e, 'support_url')}
            />

            <Input
              {...register('privacy_terms_url', {
                setValueAs: (v) => v?.trim().replace(/\s/g, ''),
              })}
              label={t('onboarding:user_privacy_terms_page')}
              placeholder={t('onboarding:user_privacy_terms_page_placeholder')}
              helperText={errors?.privacy_terms_url?.message}
              data-cy="privacy-terms-url-input"
              className="mt-6 lg:mt-0"
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://'
                  setValue('privacy_terms_url', 'https://')
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e)
                setValue('privacy_terms_url', finalValue)
              }}
              onBlur={(e) => handleUrlBlur(e, 'privacy_terms_url')}
            />
          </section>
        )}

        <div className="flex justify-center gap-6 mt-8">
          <Button type="button" onClick={handleBack} variant="ghost">
            {t('common:back')}
          </Button>
          <Button
            type="button"
            data-cy="next"
            onClick={handleNext}
            variant="default"
            disabled={isCheckingFirstProduct}
          >
            {t('next')}
          </Button>
        </div>
      </div>
    </section>
  )
}
